/**
 * Canonical role-based email addresses for shariqrefai.com.
 *
 * The domain has a catch-all: every message to *@shariqrefai.com lands in
 * one inbox. The point of using role addresses instead of one generic
 * support@ address is:
 *
 *   1. Professional presentation on the site (journalists don't want to
 *      email "support" when they mean "media").
 *   2. Sender-side filtering: someone who emails media@ signals what the
 *      inquiry is even before the reader opens it.
 *   3. Inbox filtering: the owner can label incoming mail by role.
 *   4. Future flexibility: if any role is later handed off to a human other
 *      than Dr. Refai, only the DNS/forwarding config changes. The site
 *      keeps working.
 *
 * When adding a new role address, add it here and to the domain's
 * catch-all if not already covered.
 */

export const EMAILS = {
  /** General first-touch, homepage, footer, community, "other" contact form. */
  hello: "hello@shariqrefai.com",
  /** Press interviews, journalist requests, podcast bookings, quote requests. */
  media: "media@shariqrefai.com",
  /** Keynote, event, and conference bookings. */
  speaking: "speaking@shariqrefai.com",
  /** Editorial questions, source questions, AI-use questions, medical review contact. */
  editor: "editor@shariqrefai.com",
  /** Factual corrections. */
  corrections: "corrections@shariqrefai.com",
  /** Book publishing, professional collaborations, network partnerships. */
  partnerships: "partnerships@shariqrefai.com",
  /** Notifications signup (essay + book announcements). */
  notifications: "notifications@shariqrefai.com",
  /** Privacy inquiries, DSR requests, GDPR/CCPA requests. */
  privacy: "privacy@shariqrefai.com",
  /** Accessibility feedback. */
  accessibility: "accessibility@shariqrefai.com",
  /** DMCA and copyright inquiries. */
  copyright: "copyright@shariqrefai.com",
  /** Vulnerability disclosure per RFC 9116. */
  security: "security@shariqrefai.com",
  /** Fallback for anything that doesn't fit above. */
  support: "support@shariqrefai.com",
} as const;

export type RoleEmail = keyof typeof EMAILS;

/**
 * Map from the /contact form's `inquiryType` field values to the correct
 * role address. Any unmapped value falls through to hello@.
 */
export const CONTACT_INQUIRY_TO_ROLE: Record<string, RoleEmail> = {
  media: "media",
  speaking: "speaking",
  publishing: "partnerships",
  collaboration: "partnerships",
  other: "hello",
};

/** Resolve a contact-form inquiry type to a full role address. */
export function emailForInquiry(inquiryType: string): string {
  const role = CONTACT_INQUIRY_TO_ROLE[inquiryType] ?? "hello";
  return EMAILS[role];
}
